<template>
  <v-container>
    <v-toolbar color="transparent" flat class="mb-3">
      <v-toolbar-title class="text-xl-h4 text-lg-h5 font-weight-bold">
       {{ $t('Users.List.header') }}
      </v-toolbar-title>

      <v-spacer></v-spacer>

      <v-dialog v-model="showGroups" max-width="700px">
        <template v-slot:activator="{ on }">
          <v-btn color="primary" dark class="mb-2 mr-3" v-on="on">
            <v-icon left>groups</v-icon>
            {{$t('Users.Button.manageGroups')}}
          </v-btn>
        </template>
        <UserGroupsManager/>
      </v-dialog>
      <v-dialog v-model="showEditor" max-width="700px" persistent>
        <template v-slot:activator="{ on }">
          <v-btn color="primary" dark class="mb-2" v-on="on">{{$t('Users.Button.add')}}</v-btn>
        </template>
        <UserEditor 
          v-if="showEditor"
          ref="userEditor"
          :defaultUser="editedIndex >= 0 ? usersList[editedIndex] : null" 
          @save="save"
          @cancel="close"
          :loading="saving"
        />
      </v-dialog>
    </v-toolbar>

    <v-row justify="space-around">
      <v-col cols="6">
        <SearchField 
          @search="value => search = value" 
          :help="$t('Users.List.searchHint')"
        />
      </v-col>
      <v-col cols="12">
        <v-card>
          <v-data-table
            :headers="headers"
            :items="filteredUsers"
            :items-per-page="$vuetify.breakpoint.lgAndDown ? 8 : 12"
            :footer-props="{'items-per-page-options': [8, 12, 25, 50]}"
            sort-by="email"
            class="pt-5"
          >
            <template v-slot:item.avatar>
              <v-icon>account_circle</v-icon>
            </template>
            <template v-slot:item.roles="{ item }">
              <v-icon v-if="item.isAdmin">check</v-icon>
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ item.updatedAtFormatted }}
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ item.createdAtFormatted }}
            </template>
            <template v-slot:item.lastVisitedAt="{ item }">
              {{ item.lastVisitedAtFormatted }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn small icon @click="switchTo(item)" color="primary" :disabled="item.id === $store.state.auth.user.id">
                <v-icon small>switch_account</v-icon>
              </v-btn>
              <v-btn small icon @click="editItem(item)" color="secondary">
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn small icon @click="deleteItem(item)" color="secondary">
                <v-icon small>delete</v-icon>
              </v-btn>
            </template>
            <template v-slot:no-data>
              <v-btn color="primary" @click="initialize">Reset</v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import UserEditor from '@/components/User/UserEditor'
import UserGroupsManager from '@/components/User/UserGroupsManager'
import SearchField from '@/components/Utils/SearchField'
import { FileMixin, DateMixin } from '@/mixins/index'
import { matchTextOnObject } from '@/helpers/search'
import { isAdmin } from '@/config/roles'
import { AuthService } from '@portal/api'

export default {
  components: { UserEditor, UserGroupsManager, SearchField },
  mixins: [FileMixin, DateMixin],
  data: () => ({
    showEditor: false,
    showGroups: false,
    search: '',
    editedIndex: -1,
    saving: false
  }),

  computed: {
    ...mapState('user', [
      'usersList'
    ]),
    formattedUsers () {
      return this.usersList.map(user => ({
        ...user,
        isAdmin: isAdmin(user),
        updatedAtFormatted: this.$_date_format(user.updatedAt),
        createdAtFormatted: this.$_date_format(user.createdAt),
        lastVisitedAtFormatted: user.lastVisitedAt ? this.$_date_formatDistance(user.lastVisitedAt) : null
      }))
    },
    filteredUsers () {
      return this.formattedUsers.filter(user => {
        return matchTextOnObject(this.search, user, ['email', 'firstName', 'lastName', 'brand'])
      })
    },
    headers () {
      return [
        { sortable: false, value: 'avatar', width: '50px' },
        { text: this.$t('Users.Form.email'), align: 'left', value: 'email' },
        { text: this.$t('Users.Form.firstname'), value: 'firstName' },
        { text: this.$t('Users.Form.lastname'), value: 'lastName' },
        { text: this.$t('Users.Form.admin'), value: 'roles' },
        { text: this.$t('Global.Common.updatedAt'), value: 'updatedAt' },
        { text: this.$t('Global.Common.createdAt'), value: 'createdAt' },
        { text: this.$t('Global.Common.connectedAt'), value: 'lastVisitedAt' },
        { sortable: false, value: 'actions', width: '150px' },
      ]
    }
  },

  watch: {
    showEditor (val) {
      val || this.close()
    }
  },

  created () {
    this.initialize()
  },

  methods: {
    ...mapActions('user', [
      'loadUsers',
      'createUser',
      'updateUser',
      'deleteUser'
    ]),
    async initialize () {
      this.loadUsers()
    },

    editItem (item) {
      this.editedIndex = this.usersList.findIndex(user => user.id === item.id)
      this.showEditor = true
    },

    async deleteItem (item) {
      confirm(this.$t('Users.Message.delete')) && this.deleteUser(item.id)
    },

    close () {
      this.showEditor = false
      this.$refs.userEditor.resetForm()
      setTimeout(() => {
        this.editedIndex = -1
      }, 300)
    },

    async save (user) {
      this.saving = true
      if (this.editedIndex > -1) {
        try {
          await this.updateUser(user)
          this.close()
        } catch (error) {
          this.$store.dispatch('notify/error', this.$t('Message.Error.update'))
        }
      } else {
        try {
          await this.createUser(user)
          this.close()
        } catch (error) {
          error === 'users limit reached' 
          ? this.$store.dispatch('notify/error', this.$t('Users.Message.limitReached'))
          : this.$store.dispatch('notify/error', this.$t('Message.Error.create'))
        }
      }
      this.saving = false
    },

    async switchTo (user) {
      await AuthService.switchTo(user.email)
      document.location.reload()
    }
  }
}
</script>

<style>
.theme--light.v-tabs-items {
  background-color: transparent!important;
}
</style>